import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RepHeader } from '../RepHeader';
import { repConfig } from '../../../config';
import { CheckCircle2, MessageSquare, Zap } from '../../ui/Icons';
import { Button } from '../../ui/Button';

export function StepConfirm({ data }) {
  const [timeLeft, setTimeLeft] = useState(5);
  const REDIRECT_URL = "https://calendly.com/nicole1stewart/30min";

  useEffect(() => {
    // If timer reaches 0, trigger redirect
    if (timeLeft === 0) {
      window.location.href = REDIRECT_URL;
      return;
    }

    // Set up the countdown interval
    const timer = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    // Cleanup timeout on unmount or before next effect
    return () => clearTimeout(timer);
  }, [timeLeft]);

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center text-center space-y-4 md:space-y-10 py-4 md:py-12 overflow-y-auto max-h-full">
      <motion.div 
        initial={{ scale: 0, rotate: -45 }} 
        animate={{ scale: 1, rotate: 0 }} 
        transition={{ type: "spring", damping: 15, stiffness: 200, delay: 0.2 }}
        className="w-16 h-16 md:w-24 md:h-24 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 shrink-0"
      >
        <CheckCircle2 size={32} className="md:w-12 md:h-12" />
      </motion.div>

      <div className="space-y-2 md:space-y-4">
        <h1 className="font-heading text-2xl md:text-5xl font-bold px-2">
          You're all set, {data.firstName}!
        </h1>
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm md:text-xl text-gray-400 max-w-lg mx-auto px-4 leading-relaxed">
            {repConfig.name} will personally reach out shortly.
          </p>
          
          {/* Countdown Indicator */}
          <div className="mt-4 flex flex-col items-center gap-3">
            <div className="flex items-center gap-2 text-brand-blue font-bold text-sm md:text-base bg-brand-blue/10 px-4 py-2 rounded-full border border-brand-blue/20">
              <Zap size={16} className={timeLeft > 0 ? "animate-pulse" : ""} />
              {timeLeft > 0 
                ? `Redirecting to schedule in ${timeLeft}s...` 
                : "Redirecting now..."
              }
            </div>
            {timeLeft === 0 && (
              <a 
                href={REDIRECT_URL} 
                className="text-xs text-brand-blue underline hover:text-blue-400 transition-colors"
              >
                Click here if you aren't redirected automatically
              </a>
            )}
            <p className="text-[10px] text-gray-500 italic">
              Taking you to Nicole's calendar now
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-2">
        <RepHeader variant="large" />
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 w-full space-y-3 md:space-y-4 mb-4">
        <div className="space-y-1">
          <h4 className="font-semibold text-base md:text-lg">Can't wait?</h4>
          <p className="text-gray-400 text-[11px] md:text-sm">Send a quick text to let {repConfig.name} know you're ready.</p>
        </div>
        <a 
          href={`sms:${repConfig.phoneClean}?body=Hi ${repConfig.name}, I just completed your vehicle search quiz online!`}
          className="block w-full"
        >
          <Button variant="secondary" className="w-full bg-white/10 hover:bg-white/20 h-11 md:h-14">
            <MessageSquare size={16} className="md:w-5 md:h-5" /> Text {repConfig.name} Directly
          </Button>
        </a>
      </div>
    </div>
  );
}